/* Formula comparison widget (snippets/ob-compare-widget.liquid).
   Up to 3 formulas side by side. Each formula's data is fetched once from
   /products/<handle>?section_id=ob-formula-data (sections/ob-formula-data.liquid).
   Nutrition labels differ by brand ("Fat", "Fat of which", "Total fat"; per 100 g / 100 ml pairs),
   so ROWS maps each label line to one standard row and always keeps the per-100 ml figure.
   State lives in ?compare=a,b,c so a comparison can be shared. */
(() => {
  'use strict';
  const MAX = 3;
  const root = document.querySelector('[data-obr-cw]');
  if (!root) return;

  const board = root.querySelector('[data-cw-table]');
  const empty = root.querySelector('[data-cw-empty]');
  const tools = root.querySelector('[data-cw-tools]');
  const diffBox = root.querySelector('[data-cw-diff]');
  const copied = root.querySelector('[data-cw-copied]');
  const optionsHTML = root.querySelector('[data-cw-options]').innerHTML;
  let LABELS = {};
  try { LABELS = JSON.parse(document.querySelector('[data-obr-cw-labels]').textContent); } catch (e) { /* labels fall back to keys */ }

  const cache = new Map();
  let picked = [];

  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const norm = (k) => k.toLowerCase().replace(/[*:†]/g, '').replace(/\s+/g, ' ').trim();

  /* ---------- Nutrition normalisation ---------- */
  const ROWS = [
    { k: 'energy', label: 'Energy', test: (k) => /energ|calorific/.test(k) },
    { k: 'protein', label: 'Protein', test: (k) => /^protein/.test(k) },
    { k: 'fat', label: 'Fat', test: (k) => /^(total )?fat(,? of which)?$/.test(k) || k === 'fat of which' },
    { k: 'sat', label: 'Saturated fat', sub: true, test: (k) => /saturat/.test(k) && !/unsat|mono|poly/.test(k) },
    { k: 'carbs', label: 'Carbohydrates', test: (k) => /^(total )?carbohydrates?(,? of which)?$/.test(k) },
    { k: 'lactose', label: 'Lactose', sub: true, test: (k) => /^(of which )?lactose$|of which lactose|sugar \(lactose\)/.test(k) },
    { k: 'fibre', label: 'Fibre', test: (k) => /^fib(re|er|res)\b/.test(k) },
    { k: 'dha', label: 'DHA (omega-3)', test: (k) => /dha|docosahexa/.test(k) },
    { k: 'ara', label: 'ARA (omega-6)', test: (k) => /\bara\b|arachidonic|\(aa\)/.test(k) },
    { k: 'ala', label: 'Alpha-linolenic acid', test: (k) => /linolenic|\(ala\)/.test(k) },
    { k: 'la', label: 'Linoleic acid', test: (k) => /^linoleic/.test(k) },
    { k: 'calcium', label: 'Calcium', test: (k) => /^calcium/.test(k) },
    { k: 'iron', label: 'Iron', test: (k) => /^iron/.test(k) },
    { k: 'zinc', label: 'Zinc', test: (k) => /^zinc/.test(k) },
    { k: 'iodine', label: 'Iodine', test: (k) => /^iodine/.test(k) },
    { k: 'vitd', label: 'Vitamin D', test: (k) => /^vitamin d/.test(k) },
    { k: 'vitc', label: 'Vitamin C', test: (k) => /^vitamin c/.test(k) },
    { k: 'choline', label: 'Choline', test: (k) => /^cholin/.test(k) },
  ];

  function nutrition(f) {
    const out = {};
    const head = (f.nut.find(([k, v]) => !v) || [''])[0].toLowerCase();
    const dual = /100 ?g\s*\/\s*100 ?ml/.test(head) || f.nut.some(([k, v]) => /100 ?g\s*\/\s*100 ?ml/i.test(k));
    f.nut.forEach(([rawK, rawV]) => {
      if (!rawV) return;
      const k = norm(rawK.replace(/^nutritional (facts|values) per 100 ?(g \/ 100 )?ml\*?/i, ''));
      const row = ROWS.find((r) => !out[r.k] && r.test(k));
      if (!row) return;
      let v = rawV.replace(/\*/g, '').trim();
      if (row.k === 'energy') {
        const nums = [...v.matchAll(/([\d.,]+)\s*kcal/gi)].map((m) => parseFloat(m[1].replace(',', '.')));
        const kc = nums.find((n) => n >= 40 && n <= 100);
        if (kc) out.energy = { text: `${kc} kcal`, n: kc };
        return;
      }
      // "27.7 g / 3.6 g" style pairs are per 100 g / per 100 ml: keep the per-100 ml side.
      if (v.includes('/') && (dual || /^[\d.,]+\s*[a-zµμ]*\s*\/\s*[\d.,]+/i.test(v))) v = v.split('/').pop().trim();
      const n = parseFloat(v.replace(',', '.'));
      if (Number.isNaN(n)) return;
      const unit = ((v.match(/(mg|µg|μg|mcg|g|iu)(?![a-z])/i) || [''])[0]).toLowerCase();
      // Bars compare in one unit (mg) even when labels mix g, mg and µg.
      // ARA and DHA are a few mg per 100 ml; a label saying "13.2 g" is a typo for mg (HiPP Comfort).
      const typo = (row.k === 'ara' || row.k === 'dha') && unit === 'g' && n >= 1;
      const mg = typo ? n : unit === 'g' ? n * 1000 : unit === 'mg' ? n : /µg|μg|mcg/.test(unit) ? n / 1000 : null;
      out[row.k] = { text: v, n: mg == null ? n : mg, unit: mg == null ? unit : 'mg' };
    });
    if (!out.energy && f.kcal) out.energy = { text: `${f.kcal} kcal`, n: +f.kcal };
    return out;
  }

  async function load(h) {
    if (cache.has(h)) return cache.get(h);
    const p = fetch(`/products/${encodeURIComponent(h)}?section_id=ob-formula-data`, { credentials: 'same-origin' })
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.text(); })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const f = JSON.parse(doc.querySelector('[data-obr-formula]').textContent);
        f.n = nutrition(f);
        f.traits = (f.traits || []).filter(Boolean);
        f.notes = (f.notes || []).map((x) => x.trim()).filter(Boolean);
        return f;
      });
    cache.set(h, p);
    p.catch(() => cache.delete(h));
    return p;
  }

  /* ---------- Rendering ---------- */
  const yes = '<span class="obr-yes"><svg class="obr-ico" viewBox="0 0 24 24" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg><span class="obc-sr">Yes</span></span>';
  const no = '<span class="obr-no" aria-hidden="true"></span><span class="obc-sr">No</span>';

  function cell(html, cls = '') { return `<div class="obr-cw__cell ${cls}">${html}</div>`; }
  function row(label, cells, opts = {}) {
    const same = cells.length > 1 && cells.every((c) => c.key === cells[0].key);
    const filler = cell('', 'is-filler').repeat(cols(picked.length) - picked.length);
    return `<div class="obr-cw__row${same ? ' is-same' : ''}${opts.sub ? ' is-sub' : ''}" role="row">
      <div class="obr-cw__label" role="rowheader">${label}</div>
      ${cells.map((c) => cell(c.html, c.cls)).join('')}${filler}
    </div>`;
  }
  function group(title) { return `<div class="obr-cw__group" role="row"><div role="columnheader">${title}</div></div>`; }

  // Always at least 2 columns, so even an empty widget reads as a comparison; a third while room.
  const cols = (n) => Math.max(2, Math.min(MAX, n + 1));

  function render(list) {
    const n = list.length;
    root.style.setProperty('--cw-cols', cols(n));
    empty.hidden = n > 0;
    tools.hidden = n < 2;

    const addOne = (i) => `<div class="obr-cw__add">
        <label class="obr-cw__pick">
          <span class="obr-cw__pick-icon" aria-hidden="true"><svg class="obr-ico" viewBox="0 0 24 24"><path d="M12 5v14M5 12h14"/></svg></span>
          <span class="obr-cw__pick-text">${n + i === 0 ? 'Add a formula' : n + i === 1 ? 'Add a second formula' : 'Add a third (optional)'}</span>
          <select data-cw-select aria-label="Add a formula to compare">${optionsHTML}</select>
        </label>
      </div>`;
    const addCell = Array.from({ length: cols(n) - n }, (_, i) => addOne(i)).join('');

    const cards = list.map((f) => `<div class="obr-cw__card">
        <button type="button" class="obr-cw__remove" data-cw-remove="${esc(f.handle)}" aria-label="Remove ${esc(f.title)}"><svg class="obr-ico" viewBox="0 0 24 24" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg></button>
        <span class="obr-cw__img">${f.image ? `<img src="${esc(f.image)}" alt="" width="96" height="96" loading="lazy">` : ''}</span>
        <span class="obr-cw__line">${esc(f.line)}</span>
        <a class="obr-cw__title" href="${esc(f.url)}" data-obr-sheet="${esc(f.handle)}">${esc(f.title)}</a>
        <span class="obr-cw__price">${esc(f.price)}${f.available ? '' : ' · Sold out'}</span>
        <a class="obc-btn obc-btn--solid obr-cw__buy" href="${esc(f.url)}">View product</a>
      </div>`).join('');

    let html = `<div class="obr-cw__grid" role="table" aria-label="Formula comparison">
      <div class="obr-cw__row obr-cw__row--cards" role="row"><div class="obr-cw__label" aria-hidden="true"></div>${cards}${addCell}</div>`;

    if (n) {
      html += group('At a glance');
      html += row('Age', list.map((f) => ({ html: esc(f.age), key: f.age })));
      html += row('Milk', list.map((f) => { const v = f.milk + (f.traits.includes('a2') ? ' (A2)' : ''); return { html: esc(v), key: v }; }));
      html += row('Organic', list.map((f) => { const y = f.traits.includes('organic'); return { html: y ? yes : no, key: y }; }));
      html += row('Format', list.map((f) => ({ html: esc(f.format), key: f.format })));
      if (list.some((f) => f.notes.length)) {
        html += row('Good to know', list.map((f) => ({
          html: f.notes.length ? `<ul class="obr-cw__notes" role="list">${f.notes.map((x) => `<li>${esc(x)}</li>`).join('')}</ul>` : no,
          key: f.notes.join(';'),
        })));
      }

      html += group('Per 100 ml, ready to drink');
      ROWS.forEach((r) => {
        const vals = list.map((f) => f.n[r.k]);
        if (!vals.some(Boolean)) return;
        const units = new Set(vals.filter(Boolean).map((v) => v.unit || 'kcal'));
        const max = units.size === 1 ? Math.max(...vals.filter(Boolean).map((v) => v.n)) : 0;
        html += row(r.label, vals.map((v) => {
          if (!v) return { html: '<span class="obr-cw__na">Not listed</span>', key: '–' };
          const bar = max > 0 && n > 1 ? `<span class="obr-cw__bar" aria-hidden="true"><span style="width:${Math.max(4, (v.n / max) * 100)}%"></span></span>` : '';
          return { html: `<span class="obr-cw__num">${esc(v.text)}</span>${bar}`, key: v.text.replace(/\s/g, '') };
        }), { sub: r.sub });
      });

      html += group('On the label');
      const LABEL_ROWS = [
        ['Prebiotics', (t) => ['gos', 'fos', 'hmo'].filter((k) => t.includes(k)).map((k) => k.toUpperCase()).join(', ')],
        ['Probiotics', (t) => t.includes('probiotic')],
        ['Lactose only', (t) => t.includes('lactoseonly')],
        ['Starch', (t) => t.includes('starch')],
        ['Maltodextrin', (t) => t.includes('malto')],
        ['Palm oil', (t) => t.includes('palm')],
        ['Coconut oil', (t) => t.includes('coconut')],
        ['Whole milk fat', (t) => t.includes('wholemilk')],
        ['Added ARA', (t) => t.includes('ara')],
        ['DHA from', (t) => [t.includes('fish') && 'Fish oil', t.includes('algae') && 'Algae oil'].filter(Boolean).join(' + ') || 'Source not stated'],
        ['Soy', (t) => t.includes('soy')],
      ];
      LABEL_ROWS.forEach(([label, fn]) => {
        html += row(label, list.map((f) => {
          const v = fn(f.traits);
          if (typeof v === 'string') return { html: v ? `<span class="obr-cw__txt">${esc(v)}</span>` : no, key: v };
          return { html: v ? yes : no, key: v };
        }));
      });

    }
    html += '</div>';
    if (n > 1) {
      // Full width blocks (stacked on phones): pills and long lists don't fit a narrow column.
      html += `<h3 class="obr-cw__group obr-cw__h">Only in this formula</h3><div class="obr-cw__only" style="--cw-n:${n}">${list.map((f) => {
        const others = list.filter((o) => o !== f);
        const only = f.traits.filter((t) => LABELS[t] && t !== 'lactoseonly' && others.every((o) => !o.traits.includes(t)));
        return `<div class="obr-cw__only-item">
          <p class="obr-cw__only-name">${esc(f.title)}</p>
          ${only.length ? `<ul class="obr-tags" role="list">${only.map((t) => `<li class="obr-tag obr-tag--${esc(LABELS[t][1])}">${esc(LABELS[t][0])}</li>`).join('')}</ul>` : '<p class="obr-cw__na">Nothing the others don’t also list</p>'}
        </div>`;
      }).join('')}</div>`;
    }
    if (n) {
      html += `<h3 class="obr-cw__group obr-cw__h">Full ingredient list</h3>`;
      html += `<div class="obr-cw__ingrs" style="--cw-n:${n}">${list.map((f) => `
        <details class="obr-cw__ingr">
          <summary><span>${esc(f.title)}</span><svg class="obr-ico" viewBox="0 0 24 24" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg></summary>
          <p>${esc(f.ingredients.replace(/^ingredients\s*/i, ''))}</p>
        </details>`).join('')}</div>`;
    }
    board.innerHTML = html;
    board.classList.toggle('is-diff', diffBox.checked);
  }

  let token = 0;
  async function refresh() {
    const my = ++token;
    writeUrl();
    if (!picked.length) { render([]); return; }
    board.setAttribute('aria-busy', 'true');
    board.classList.add('is-loading');
    try {
      const list = (await Promise.allSettled(picked.map(load))).map((r) => (r.status === 'fulfilled' ? r.value : null));
      if (my !== token) return;
      const ok = list.filter(Boolean);
      picked = ok.map((f) => f.handle);
      render(ok);
    } finally {
      board.removeAttribute('aria-busy');
      board.classList.remove('is-loading');
    }
  }

  function add(h, scroll) {
    if (!h || picked.includes(h)) return;
    if (picked.length >= MAX) picked.pop();
    picked.push(h);
    refresh();
    if (scroll) root.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
  }

  function writeUrl() {
    if (!window.OBR) return;
    window.OBR.setParams((p) => { if (picked.length) p.set('compare', picked.join(',')); else p.delete('compare'); });
  }

  root.addEventListener('change', (e) => {
    const s = e.target.closest('[data-cw-select]');
    if (s && s.value) add(s.value);
  });
  root.addEventListener('click', (e) => {
    const r = e.target.closest('[data-cw-remove]');
    if (r) { picked = picked.filter((h) => h !== r.dataset.cwRemove); refresh(); return; }
    const pr = e.target.closest('[data-cw-preset]');
    if (pr) { picked = pr.dataset.cwPreset.split(',').slice(0, MAX); refresh(); }
  });
  diffBox.addEventListener('change', () => board.classList.toggle('is-diff', diffBox.checked));
  root.querySelector('[data-cw-copy]').addEventListener('click', async () => {
    try { await navigator.clipboard.writeText(location.href); copied.textContent = 'Link copied'; }
    catch (e) { copied.textContent = 'Copy the address bar to share'; }
    setTimeout(() => { copied.textContent = ''; }, 2500);
  });
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-obr-compare-add]');
    if (!b) return;
    e.preventDefault();
    add(b.dataset.obrCompareAdd, true);
  });

  const fromUrl = (new URLSearchParams(location.search).get('compare') || '').split(',').filter((h) => /^[a-z0-9-]+$/.test(h)).slice(0, MAX);
  picked = fromUrl.length ? fromUrl : (root.dataset.current ? [root.dataset.current] : []);
  refresh();
})();
